import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import zxcvbn from 'zxcvbn';

type PasswordFieldProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  name?: string;
  placeholder?: string;
  autoComplete?: 'current-password' | 'new-password';
  emailForChecks?: string;
  showStrength?: boolean;
  showInlineChecklist?: boolean;
  showHelp?: boolean;
  withConfirm?: boolean;
  confirmValue?: string;
  onConfirmChange?: (value: string) => void;
  onValidityChange?: (isValid: boolean, reasons: string[]) => void;
  disabled?: boolean;
  error?: string;
};

const FieldWrap = styled.div`
  position: relative;
`;

const Label = styled.div`
  color: #00d4ff;
  font-size: 13px;
  margin-bottom: 5px;
  letter-spacing: 1px;
  text-align: left;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  padding: 10px 38px 10px 12px;
  outline: none;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
`;

const Toggle = styled.button`
  position: absolute;
  right: 8px;
  top: 30px;
  background: none;
  border: none;
  color: #00d4ff;
  padding: 0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HelpBtn = styled.button`
  position: absolute;
  right: 40px;
  top: 30px;
  background: none;
  border: none;
  color: #00d4ff;
  padding: 0;
  font-size: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const HelpPanel = styled.div`
  position: absolute;
  right: 0;
  top: 64px;
  z-index: 5;
  background: rgba(0, 20, 30, 0.95);
  border: 1px solid #00d4ff55;
  border-radius: 8px;
  padding: 10px 12px;
  min-width: 260px;
  box-shadow: 0 6px 20px rgba(0, 212, 255, 0.15);
`;

const Error = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 6px;
`;

const StrengthBar = styled.div<{ score: number; isValid: boolean }>`
  margin-top: 8px;
  height: 6px;
  border-radius: 3px;
  background: ${({ score, isValid }) => {
    // Зелёный только если выполнены правила и zxcvbn ≥ 2 (умеренно строгий порог)
    if (score >= 2 && isValid) return 'linear-gradient(90deg, #00d4ff 0%, #00aa88 100%)';
    if (score >= 2) return 'linear-gradient(90deg, #ffb833 0%, #8a5a00 100%)';
    if (score >= 1) return 'linear-gradient(90deg, #ff5c33 0%, #552211 100%)';
    return 'linear-gradient(90deg, #ff2d2d 0%, #441111 100%)';
  }};
`;

const List = styled.ul`
  margin: 8px 0 0 18px;
  padding: 0;
  color: #b0b0b0;
  font-size: 12px;
`;

const PasswordField: React.FC<PasswordFieldProps> = ({
  value,
  onChange,
  label = 'Password',
  name = 'password',
  placeholder = 'Enter your password',
  autoComplete = 'new-password',
  emailForChecks,
  showStrength = true,
  showInlineChecklist = false,
  showHelp = false,
  withConfirm = false,
  confirmValue = '',
  onConfirmChange,
  onValidityChange,
  disabled,
  error,
}) => {
  const [visible, setVisible] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const rules = useMemo(() => {
    const reasons: string[] = [];
    const hasMin = value.length >= 8;
    if (!hasMin) reasons.push('At least 8 characters');

    const hasLower = /[a-z]/.test(value);
    if (!hasLower) reasons.push('Must contain lowercase letter');

    const hasUpper = /[A-Z]/.test(value);
    if (!hasUpper) reasons.push('Must contain uppercase letter');

    const hasDigit = /\d/.test(value);
    if (!hasDigit) reasons.push('Must contain a digit');

    const hasSymbol = /[^A-Za-z0-9]/.test(value);
    if (!hasSymbol) reasons.push('Must contain a symbol');

    const noSpaces = !/\s/.test(value);
    if (!noSpaces) reasons.push('No spaces allowed');

    const onlyEnRu = /^[A-Za-z\u0400-\u04FF0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]*$/.test(value);
    if (!onlyEnRu) reasons.push('Use only English/Russian letters, digits and symbols');
    const notEmail = emailForChecks ? value.toLowerCase() !== emailForChecks.toLowerCase() : true;
    if (!notEmail) reasons.push('Password must not equal email');

    const confirmOk = withConfirm ? value === confirmValue && confirmValue.length > 0 : true;
    if (withConfirm && !confirmOk) reasons.push('Passwords do not match');

    const coreValid = hasMin && hasLower && hasUpper && hasDigit && hasSymbol && noSpaces && onlyEnRu && notEmail;
    const valid = withConfirm ? (coreValid && confirmOk) : coreValid;
    return { valid, coreValid, reasons } as { valid: boolean; coreValid: boolean; reasons: string[] };
  }, [value, withConfirm, confirmValue, emailForChecks]);

  useEffect(() => {
    onValidityChange?.(rules.valid, rules.reasons);
  }, [rules, onValidityChange]);

  const strength = useMemo(() => zxcvbn(value || '').score, [value]);

  return (
    <FieldWrap>
      {label && <Label>{label}</Label>}
      <Input
        type={visible ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={e => {
          const next = e.target.value.replace(/[^A-Za-z\u0400-\u04FF0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/g, '');
          onChange(next);
        }}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        inputMode="text"
      />
      <Toggle type="button" aria-label={visible ? 'Hide password' : 'Show password'} onClick={() => setVisible(v => !v)}>
        {visible ? (
          // eye-off icon
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-8 1.02-2.86 2.99-5.26 5.5-6.82"/>
            <path d="M1 1l22 22"/>
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
            <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c5 0 9.27 3.11 11 8a11.72 11.72 0 0 1-2.16 3.19"/>
          </svg>
        ) : (
          // eye icon
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </Toggle>
      {showHelp && (
        <HelpBtn type="button" aria-label="Password requirements" onClick={() => setHelpOpen(v=>!v)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 2-3 4"/>
            <line x1="12" y1="17" x2="12" y2="17"/>
          </svg>
        </HelpBtn>
      )}
      {showHelp && helpOpen && (
        <HelpPanel>
          <div style={{color:'#b0eaff', fontSize:12, marginBottom:6}}>Password requirements:</div>
          <List>
            <li>At least 8 characters</li>
            <li>Must contain lowercase letter</li>
            <li>Must contain uppercase letter</li>
            <li>Must contain a digit</li>
            <li>Must contain a symbol</li>
            <li>No spaces</li>
            <li>Must not equal your email</li>
          </List>
        </HelpPanel>
      )}

      {showStrength && <StrengthBar score={strength} isValid={(rules as any).coreValid ?? rules.valid} />}

      {showInlineChecklist && !rules.valid && (
        <List>
          {rules.reasons.map((r, idx) => (
            <li key={idx}>{r}</li>
          ))}
        </List>
      )}

      {withConfirm && onConfirmChange && (
        <div style={{ marginTop: 10 }}>
          <Label>Confirm Password</Label>
          <Input
            type={visible ? 'text' : 'password'}
            name={`${name}-confirm`}
            value={confirmValue}
            onChange={e => onConfirmChange(e.target.value.replace(/[^A-Za-z\u0400-\u04FF0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/g, ''))}
            placeholder="Repeat your password"
            autoComplete="new-password"
            disabled={disabled}
          />
        </div>
      )}

      {error && <Error>{error}</Error>}
    </FieldWrap>
  );
};

export default PasswordField;


